import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isStorageAvailable, setIsStorageAvailable] = useState(true);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true, // Always true and disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    try {
      const hasConsent = localStorage.getItem('cookieConsent');
      const savedSettings = localStorage.getItem('cookieSettings');

      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      } else if (!hasConsent) {
        setIsVisible(true);
      }
    } catch (error) {
      console.warn('localStorage er ikke tilgængelig:', error);
      setIsStorageAvailable(false);
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    if (isStorageAvailable) {
      localStorage.setItem('cookieConsent', 'true');
      localStorage.setItem('cookieSettings', JSON.stringify({
        ...settings,
        analytics: true,
        marketing: true,
      }));
    }
    setIsVisible(false);
  };

  const handleReject = () => {
    if (isStorageAvailable) {
      localStorage.setItem('cookieConsent', 'false');
      localStorage.setItem('cookieSettings', JSON.stringify({
        ...settings,
        analytics: false,
        marketing: false,
      }));
    }
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    if (isStorageAvailable) {
      localStorage.setItem('cookieConsent', 'custom');
      localStorage.setItem('cookieSettings', JSON.stringify(settings));
    }
    setIsVisible(false);
  };

  const toggleSetting = (key: keyof Omit<CookieSettings, 'necessary'>) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-50 max-h-[90vh] overflow-y-auto">
      <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Cookie className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-gray-600 flex-grow">
            Vi bruger cookies til at forbedre din oplevelse på vores hjemmeside.
          </p>
          <button
            onClick={() => setIsVisible(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Luk cookie besked"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {showSettings ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Nødvendige cookies</h3>
                  <p className="text-sm text-gray-600">Krævet for grundlæggende funktionalitet</p>
                </div>
                <div className="relative">
                  <button
                    className="w-11 h-6 bg-green-600 rounded-full cursor-not-allowed"
                    disabled
                  >
                    <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full transition-transform" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Analyse cookies</h3>
                  <p className="text-sm text-gray-600">Hjælper os med at forbedre websitet</p>
                </div>
                <button
                  onClick={() => toggleSetting('analytics')}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    settings.analytics ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span 
                    className={`block w-4 h-4 bg-white rounded-full transition-transform ${
                      settings.analytics ? 'translate-x-6' : 'translate-x-1'
                    }`} 
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Marketing cookies</h3>
                  <p className="text-sm text-gray-600">Bruges til målrettet markedsføring</p>
                </div>
                <button
                  onClick={() => toggleSetting('marketing')}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    settings.marketing ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span 
                    className={`block w-4 h-4 bg-white rounded-full transition-transform ${
                      settings.marketing ? 'translate-x-6' : 'translate-x-1'
                    }`} 
                  />
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                Tilbage
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Gem indstillinger
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              onClick={handleReject}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
            >
              Afvis alle
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Tilpas indstillinger
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Acceptér alle
            </button>
          </div>
        )}
      </div>
    </div>
  );
}